import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  Typography,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RolesFilterDialog from "../../components/UserManegement/RolesFilterDialog";
import RoleDialog from "../../components/UserManegement/RoleDialog";
import RoleConfirmDialog from "../../components/UserManegement/RoleConfirmDialog";
import { searchStyles } from "../../styles/formStyles";
import { IRoleListDTO } from "../../interfaces/UserManagement";
import { getRolesList, searchFilterRoles } from "../../services/UserManagement";

const Roles = () => {
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [roles, setRoles] = useState<Array<IRoleListDTO>>([]);
  const [loading, setLoading] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [filters, setFilters] = useState<{ statuses: string[] }>({ statuses: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<IRoleListDTO | null>(null);
  const [editingRole, setEditingRole] = useState<IRoleListDTO | null>(null);
  const rowsPerPage = 7;

  useEffect(() => {
    if (searchParams.get("action") === "add") {
      setDialogOpen(true);
      setSearchParams({});
    }
  }, [searchParams]);

  const fetchRolesList = async () => {
    try {
      setLoading(true);
      const res = await getRolesList();
      setRoles(res && Array.isArray(res) ? res : []);
    } catch (error) {
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await searchFilterRoles(filters.statuses, value);
        setRoles(Array.isArray(res) ? res : []);
        setPage(1);
      } catch {
        setRoles([]);
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  useEffect(() => {
    fetchRolesList();
  }, []);

  const paginatedData = roles.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.ceil(roles.length / rowsPerPage);

  const openAddRoleDialog = () => {
    setEditingRole(null);
    setDialogOpen(true);
  };

  const openEditRoleDialog = (role: IRoleListDTO) => {
    setEditingRole(role);
    setDialogOpen(true);
  };

  const handleRoleSaved = () => {
    setDialogOpen(false);
    fetchRolesList();
  };

  const handleConfirmStatusSaved = () => {
    setSelectedRole(null);
    fetchRolesList();
  };

  return (
    <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column", p: 1, gap: 2 }}>
      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ p: 1, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, borderBottom: "1px solid", borderColor: "grey.100" }}>
          <TextField
            placeholder="Search by Role"
            size="small"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "grey.500" }} />
                </InputAdornment>
              ),
            }}
            sx={searchStyles}
          />
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              startIcon={<FilterAltOutlinedIcon />}
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              sx={{ borderRadius: "6px", border: "1px solid rgba(74, 85, 101, 0.10)", backgroundColor: "#FFF", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", textTransform: "none", color: "text.primary", px: 2, "&:hover": { backgroundColor: "#FFF" } }}
            >
              Filter {filters.statuses.length > 0 && <Typography component="span" sx={{ ml: 0.5, color: "primary.main" }}>({filters.statuses.length})</Typography>}
            </Button>
            <Button
              variant="contained"
              endIcon={<AddIcon />}
              onClick={openAddRoleDialog}
              sx={{ borderRadius: "6px", textTransform: "none", px: 2, "&:hover": { backgroundColor: "primary.main" } }}
            >
              Add Role
            </Button>
          </Box>
        </Box>

        <Box sx={{ p: 1, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Paper sx={{ maxHeight: "500px", overflow: "auto", "&::-webkit-scrollbar": { width: "8px", height: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Role no.</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Role name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Users</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No roles available</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row) => (
                    <TableRow key={row.roleId} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.roleId ?? ""}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.roleName ?? ""}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.numberOfUsers ?? ""}</TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Typography
                          sx={{
                            bgcolor: row.status === "Active" ? "success.light" : "error.light",
                            color: row.status === "Active" ? "success.main" : "error.main",
                            textAlign: "center",
                            py: 0.5,
                            px: 1,
                            borderRadius: "6px",
                            display: "inline-block",
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            minWidth: "80px",
                          }}
                        >
                          {row.status}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                          <Button
                            startIcon={<Box component="img" src="/edit.svg" alt="Edit" sx={{ width: "1.2rem", height: "1.2rem" }} />}
                            onClick={() => openEditRoleDialog(row)}
                            sx={{ color: "primary.main", textTransform: "none", fontSize: "0.8rem", minWidth: "80px", "&:hover": { backgroundColor: "transparent" } }}
                          >
                            Edit
                          </Button>
                          {row.status === "Active" ? (
                            <Button
                              startIcon={<CloseIcon />}
                              onClick={() => setSelectedRole(row)}
                              sx={{ color: "error.main", textTransform: "none", fontSize: "0.8rem", minWidth: "110px", "&:hover": { backgroundColor: "transparent" } }}
                            >
                              Inactivate
                            </Button>
                          ) : (
                            <Button
                              startIcon={<CheckIcon />}
                              onClick={() => setSelectedRole(row)}
                              sx={{ color: "success.main", textTransform: "none", fontSize: "0.8rem", minWidth: "110px", "&:hover": { backgroundColor: "transparent" } }}
                            >
                              Activate
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Paper>

      {roles.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" shape="rounded" />
        </Box>
      )}

      <RolesFilterDialog
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        search={search}
        onApply={(newFilters, data) => {
          setFilters(newFilters);
          setRoles(data);
          setPage(1);
          setFilterAnchor(null);
        }}
      />

      <RoleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleRoleSaved}
        editingRole={editingRole}
      />

      <RoleConfirmDialog
        open={!!selectedRole}
        onClose={() => setSelectedRole(null)}
        onSave={handleConfirmStatusSaved}
        role={selectedRole}
      />
    </Box>
  );
};
export default Roles;
